// Place search box (spec §9). Searches only when the person presses Search or Enter: Nominatim's
// usage policy forbids search-as-you-type. Results come through PlanA's server (paced and cached).
import { useState, type FormEvent } from 'react';
import { api, type PlaceResult } from '../api.js';

export function PlaceSearch({ projectId, onPick, autoFocus = false }: { projectId: string; onPick: (r: PlaceResult) => void; autoFocus?: boolean }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<PlaceResult[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const search = async (e: FormEvent) => {
    e.preventDefault();
    if (q.trim().length < 2) return;
    setBusy(true);
    setError('');
    try {
      const r = await api<{ results: PlaceResult[] }>('GET', `/api/projects/${projectId}/places/search?q=${encodeURIComponent(q.trim())}`);
      setResults(r.results);
    } catch (err) {
      setResults(null);
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="place-search" role="search" onSubmit={search}>
      <div className="row">
        <input aria-label="Search for a place" placeholder="Search for a place" value={q} autoFocus={autoFocus} onChange={(e) => setQ(e.target.value)} />
        <button type="submit" disabled={busy || q.trim().length < 2}>{busy ? 'Searching…' : 'Search'}</button>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      {results &&
        (results.length ? (
          <ul aria-label="Places found">
            {results.map((r, i) => (
              <li key={`${i}-${r.lat}-${r.lon}`}>
                <button type="button" onClick={() => onPick(r)}>
                  <strong>{r.name || r.displayName}</strong>
                  <small>{r.displayName}</small>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted small">No places found.</p>
        ))}
      <p className="muted small">Search by OpenStreetMap Nominatim.</p>
    </form>
  );
}
